import ConditionalWrapper from '@atoms/ConditionalWrapper/ConditionalWrapper';
import Icon from '@atoms/Icon/Icon';
import Text from '@atoms/Text/Text';
import SiteEnum from '@models/enums/SiteEnum';
import classNames from 'classnames';

type IPageBreadcrumb = {
  readonly href: string;
  readonly label: string;
};

export interface PageBreadcrumbProps {
  readonly items: IPageBreadcrumb[];
  readonly site?: SiteEnum;
}

const truncatedLabelLength = 20;
const maxLabelLength = 40;

const PageBreadcrumb = ({ items, site }: PageBreadcrumbProps) => {
  const itemsWithHref = items.filter((x) => x.href);
  const previousItem = itemsWithHref[itemsWithHref.length - 2];

  const formatLabel = (label: string) => {
    const isLongLabel = label.length > maxLabelLength;
    const hasManyLabels =
      items.length > 3 && label.length > truncatedLabelLength;

    if (isLongLabel || hasManyLabels) {
      return `${label.slice(0, truncatedLabelLength)}...`;
    }
    return label;
  };

  return (
    <>
      <nav
        id="breadcrumbs-mobile"
        className={classNames(
          site === SiteEnum.B2B ? 'b2b-container' : 'tw-container',
          'tw-block tw-h-[68px] tw-py-sm tw-text-grey-100 md:tw-hidden'
        )}
        aria-label="Breadcrumb"
      >
        <a
          href={previousItem?.href}
          className="tw-flex tw-items-center tw-gap-2"
        >
          <Icon name="chevron-left" />
          <Text variant="body-small">{previousItem?.label}</Text>
        </a>
      </nav>

      <nav
        id="breadcrumbs-desktop"
        aria-label="Breadcrumb"
        className="tw-hidden tw-h-[68px] tw-border-b tw-border-grey-20 tw-py-sm tw-text-grey-100 md:tw-block"
      >
        <ol
          className={classNames(
            site === SiteEnum.B2B ? 'b2b-container' : 'tw-container',
            'tw-flex tw-gap-2'
          )}
        >
          {items.map(({ label, href }, index) => {
            const isLastItem = index === items.length - 1;
            return (
              <li key={label} className="tw-flex tw-items-center tw-gap-2">
                <ConditionalWrapper
                  condition={!isLastItem && !!href}
                  wrapper={(children) => (
                    <a
                      href={href}
                      className="tw-transition-colors hover:tw-text-grey-70"
                      aria-label={label}
                    >
                      {children}
                    </a>
                  )}
                >
                  <Text
                    variant="body-small"
                    className={classNames(isLastItem && 'tw-text-grey-70')}
                  >
                    {formatLabel(label)}
                  </Text>
                </ConditionalWrapper>
                {!isLastItem && <Icon name="chevron-right" />}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};

export default PageBreadcrumb;
